import { makeStyles } from "tss-react/mui";
import {
  MiCheckbox,
  MiInputTextbox,
  MiModal,
  MiSearchInput,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { IMaterialsResponse, IMergedBidArea } from "@miplatform/types";
import { mdiSync } from "@mdi/js";
import { Box, ClickAwayListener, Typography } from "@mui/material";
import { getMaterialLabel } from "./getMaterialLabel";
import { useReplaceItemsViewModel } from "./useReplaceItemsViewModel";

export const ReplaceItemsModal = ({
  open,
  builderId,
  selectedItem,
  selectedArea,
  planAreas,
  onClose,
  onReplaceItem,
}: ReplaceItemsModalProps) => {
  const { classes, theme } = useStyles();

  const {
    search,
    isLoading,
    replacementItem,
    results,
    matchingAreas,
    selectedAreaIds,
    isAllSelected,
    showResults,
    setIsResultsOpen,
    handleSearchFocus,
    handleSearchChange,
    handleSelectReplacement,
    handleToggleArea,
    handleToggleAllAreas,
  } = useReplaceItemsViewModel({
    open,
    builderId,
    selectedItem,
    selectedArea,
    planAreas,
  });

  const handleReplace = () => {
    if (!selectedItem || !replacementItem || !selectedAreaIds.length) return;
    onReplaceItem(selectedItem, replacementItem, selectedAreaIds);
    onClose();
  };

  return (
    <MiModal
      open={open}
      closeModal={onClose}
      title="Replace Item"
      titleIcon={mdiSync}
      leftIconColor={theme.palette.primary.main}
      width={500}
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: onClose,
        },
        {
          name: "Replace",
          disabled: !replacementItem || !selectedAreaIds.length || isLoading,
          variant: BUTTON_VARIANTS.CONTAINED,
          onClick: handleReplace,
        },
      ]}
    >
      <Box className={classes.content}>
        <Box className={classes.sectionWrapper}>
          <Typography className={classes.sectionLabel}>Current item</Typography>
          <MiInputTextbox
            value={getMaterialLabel(selectedItem)}
            readOnly
            sx={{
              border: `1px solid rgba(0, 0, 0, 0.23)`,
              borderRadius: 1,
              padding: theme.spacing(0),
            }}
          />
        </Box>
        <Box className={classes.sectionWrapper}>
          <Typography className={classes.sectionLabel}>Replace with</Typography>
          <ClickAwayListener onClickAway={() => setIsResultsOpen(false)}>
            <Box className={classes.searchWrapper}>
              <MiSearchInput
                placeholder="Search items"
                value={
                  replacementItem ? getMaterialLabel(replacementItem) : search
                }
                handleChange={(e) => handleSearchChange(e.target.value)}
                onFocus={handleSearchFocus}
                style={{ marginLeft: 0 }}
                textfieldProps={{
                  style: { height: 40 },
                  autoComplete: "off",
                }}
              />
              {showResults && (
                <Box className={classes.resultsList}>
                  {results.map((material) => (
                    <Box
                      key={material.id}
                      className={classes.resultRow}
                      onClick={() => handleSelectReplacement(material)}
                    >
                      <Typography className={classes.resultText}>
                        {getMaterialLabel(material)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Box className={classes.sectionWrapper}>
          <Typography className={classes.sectionLabel}>
            Select areas to replace this item
          </Typography>
          <Box className={classes.areasGrid}>
            <MiCheckbox
              label={
                <Typography className={classes.areaLabel}>All areas</Typography>
              }
              value={isAllSelected}
              handleChange={handleToggleAllAreas}
              size="small"
            />
            {matchingAreas.map((area) => (
              <MiCheckbox
                key={area.id}
                label={
                  <Typography className={classes.areaLabel}>
                    {area.label || area.areaTypeName || ""}
                  </Typography>
                }
                value={selectedAreaIds.includes(area.id)}
                handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleToggleArea(area.id, e.target.checked)
                }
                size="small"
              />
            ))}
          </Box>
        </Box>
      </Box>
    </MiModal>
  );
};

interface ReplaceItemsModalProps {
  open: boolean;
  builderId: string;
  selectedItem: IMaterialsResponse | null;
  selectedArea: IMergedBidArea | null;
  planAreas: IMergedBidArea[];
  onClose: () => void;
  onReplaceItem: (
    selectedItem: IMaterialsResponse,
    replacementItem: IMaterialsResponse,
    selectedAreaIds: string[]
  ) => void;
}

const useStyles = makeStyles()((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  sectionWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  searchWrapper: {
    position: "relative",
  },
  resultsList: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1,
    maxHeight: 158,
    overflowY: "auto",
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    boxShadow: theme.shadows[2],
  },
  resultRow: {
    padding: theme.spacing(2, 3),
    cursor: "pointer",
    "&:not(:last-child)": {
      borderBottom: `1px solid ${theme.palette.grey.light}`,
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },
  resultText: {
    color: theme.palette.primary.main,
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.fontSize.medium,
  },
  areasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    columnGap: theme.spacing(2),
    maxHeight: 120,
    overflowY: "auto",
    "& > label > span": {
      maxHeight: 32,
    },
  },
  sectionLabel: {
    color: theme.palette.grey.main,
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.semiBold,
  },
  areaLabel: {
    fontSize: theme.fontSize.medium,
  },
}));
